import React, { useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TextToHTML from './TextToHtml';
import PackMediaModal from './PackMediaModal';
import LoadingSpinner from './LoadingSpinner';
import LikeButton from './LikeButton';

// Lista de publicaciones (feed) de una modelo, pensada para vivir dentro de
// una tab del perfil — a diferencia de FeedByUser.jsx (la página completa a
// la que hoy se navega desde el menú "Ver feeds"), acá no hay layout propio
// ni botón "All": el perfil ya provee ambas cosas.
function ProfileFeedList({ feeds, isLoading, hasMore, onLoadMore }) {
  const observerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  // El visor de fotos se abre empujando su propia entrada de historial (en
  // vez de estado local) para que "atrás" (gesto del navegador, botón
  // físico, o Escape) solo cierre el visor y no arrastre consigo el modal de
  // perfil que está debajo — mismo patrón que usa MosaicMediaModal para el
  // visor de fotos de perfil/portada y handleMediaClick para los packs. Con
  // estado local (como estaba antes con FluidMediaModal/feedmediamodal),
  // "atrás" no tenía una entrada propia que consumir y terminaba cerrando
  // directamente el modal de perfil de abajo.
  const mediaUrlsToShow = location.state?.feedMediaViewer || [];
  const mediaStartIndex = location.state?.feedMediaViewerIndex || 0;
  const showMediaModal = mediaUrlsToShow.length > 0;

  // hasMore/isLoading/onLoadMore se leen por ref DENTRO del observer en vez
  // de ir en deps de un efecto — recrear el IntersectionObserver cada vez que
  // cambia isLoading (o sea, en CADA carga: pasa a true y al toquecito vuelve
  // a false) hace que vuelva a llamarse observer.observe(), que reporta de
  // entrada el estado de intersección ACTUAL. Si el sentinel ya está a la
  // vista (lista corta que no llena la pantalla), eso disparaba onLoadMore()
  // de nuevo ahí mismo, lo que terminaba la carga con isLoading en false otra
  // vez, recreando el observer de nuevo... un loop que traía página tras
  // página sin que el usuario scrolleara un pixel.
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const getObserver = () => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
          onLoadMoreRef.current();
        }
      }, { rootMargin: '0px', threshold: 0.5 });
    }
    return observerRef.current;
  };

  // Callback ref en vez de un ref objeto + efecto: se ejecuta justo cuando el
  // DOM del sentinel se monta o desmonta, sin importar en qué momento/branch
  // condicional haya aparecido. Hace falta porque el sentinel NO existe en el
  // primer render (más abajo, "feeds.length === 0 && !isLoading" toma el
  // branch vacío hasta que el fetch dispara isLoading=true un instante
  // después) — con un efecto de deps vacías atado al montaje del componente,
  // observer.observe() se llamaba antes de que el sentinel existiera y nunca
  // se reintentaba, dejando el scroll infinito muerto para siempre.
  const sentinelRef = useCallback((node) => {
    const observer = getObserver();
    observer.disconnect();
    if (node) observer.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const handleMediaClick = (mediaUrls, index = 0) => {
    history.push({
      pathname: location.pathname,
      search: location.search,
      hash: '#feedMedia',
      state: { ...location.state, feedMediaViewer: mediaUrls, feedMediaViewerIndex: index },
    });
  };

  const closeMediaModal = () => {
    history.goBack();
  };

  if (feeds.length === 0 && !isLoading) {
    return (
      <div className="ap-subhead text-center py-16">
        Todavía no hay publicaciones.
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full">
      {feeds.map((feed) => (
        <div
          key={feed.id}
          className="ap-card relative mb-5 p-4 max-w-2xl w-full"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ boxShadow: '0 0 0 0.5px var(--ap-separator)' }}>
                <img src={feed.avatar} alt="" className="object-cover w-full h-full" />
              </div>
              <div className="ml-3">
                <div className="ap-headline capitalize">
                  {feed.user_date?.nickname}
                </div>
              </div>
            </div>
            <LikeButton feedId={feed.id} />
          </div>

          <div className="ap-body px-1 py-1">
            <TextToHTML text={feed.description} />
          </div>

          {feed.files?.length > 0 && (
            <div className={`mt-3 ${feed.files.length > 1 ? 'grid grid-cols-2 gap-2' : 'flex'}`}>
              {feed.files.map((file, index) => (
                file.url_thumbnail ? (
                  <img
                    key={index}
                    src={file.url_thumbnail}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-square object-cover cursor-pointer rounded-[14px]"
                    onClick={() => handleMediaClick(feed.files.map(f => f.url), index)}
                  />
                ) : (
                  <div key={index} className="w-full aspect-square animate-pulse rounded-[14px] flex items-center justify-center" style={{ background: 'var(--ap-fill)' }}>
                    <span className="ap-footnote">Procesando...</span>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      ))}

      <div ref={sentinelRef} className="flex justify-center items-center py-4 w-full">
        {isLoading && <LoadingSpinner />}
      </div>

      {showMediaModal && (
        <PackMediaModal
          show={showMediaModal}
          onClose={closeMediaModal}
          mediaUrls={mediaUrlsToShow}
          startIndex={mediaStartIndex}
        />
      )}
    </div>
  );
}

export default ProfileFeedList;
